import * as React from "react"

import type { SortMode } from "@/lib/songs"

export interface FilterState {
  query: string
  genres: string[]
  sort: SortMode
  favoritesOnly: boolean
}

const DEFAULT_STATE: FilterState = {
  query: "",
  genres: [],
  sort: "title",
  favoritesOnly: false,
}

function readFromUrl(): FilterState {
  const params = new URLSearchParams(window.location.search)
  const genreParam = params.get("genre")
  const sortParam = params.get("sort")

  return {
    query: params.get("q") ?? DEFAULT_STATE.query,
    genres: genreParam ? genreParam.split(",").filter(Boolean) : [],
    sort:
      sortParam === "artist" || sortParam === "random"
        ? sortParam
        : "title",
    favoritesOnly: params.get("fav") === "1",
  }
}

function writeToUrl(state: FilterState) {
  const params = new URLSearchParams()
  if (state.query) params.set("q", state.query)
  if (state.genres.length > 0) params.set("genre", state.genres.join(","))
  if (state.sort !== "title") params.set("sort", state.sort)
  if (state.favoritesOnly) params.set("fav", "1")

  const search = params.toString()
  const url = search
    ? `${window.location.pathname}?${search}`
    : window.location.pathname
  window.history.replaceState(null, "", url)
}

/** Filter/search/sort state, kept in sync with the URL query string for shareable links. */
export function useFilterState() {
  const [state, setState] = React.useState<FilterState>(() => readFromUrl())

  React.useEffect(() => {
    writeToUrl(state)
  }, [state])

  React.useEffect(() => {
    const handlePopState = () => setState(readFromUrl())
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  const setQuery = React.useCallback((query: string) => {
    setState((prev) => ({ ...prev, query }))
  }, [])

  const toggleGenre = React.useCallback((genre: string) => {
    setState((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...prev.genres, genre],
    }))
  }, [])

  const clearGenres = React.useCallback(() => {
    setState((prev) => ({ ...prev, genres: [] }))
  }, [])

  const setSort = React.useCallback((sort: SortMode) => {
    setState((prev) => ({ ...prev, sort }))
  }, [])

  const setFavoritesOnly = React.useCallback((favoritesOnly: boolean) => {
    setState((prev) => ({ ...prev, favoritesOnly }))
  }, [])

  return {
    state,
    setQuery,
    toggleGenre,
    clearGenres,
    setSort,
    setFavoritesOnly,
  }
}
